<template>
  <div v-if="buildingStatusForm">
    <div class="section-title mb-4">สถานะการก่อสร้าง</div>
    <building-status-editor
      v-model="buildingStatusForm"
      :image-urls="imageUrls"
      :disabled="isEditorDisabled"
      class="mb-5"
      @removeImage="handleRemoveImage"
    ></building-status-editor>
    <div class="actions text-end">
      <sc-button
        v-if="instruction"
        class="btn-remove"
        variant="danger"
        @click="handleClickRemove"
        >ลบ</sc-button
      >
      <sc-button
        v-if="!isEditorDisabled"
        variant="primary"
        @click="handleClickSave"
        >บันทึก</sc-button
      >
    </div>
  </div>
</template>

<script>
import { baanRueJaiClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import ScButton from "@/lib/components/ScButton";

import { MessageBox } from "element-ui";
import { DateTime } from "luxon";

import BuildingStatusEditor from "../components/BuildingStatusEditor";

export default {
  components: {
    ScButton,
    BuildingStatusEditor
  },
  data() {
    return {
      buildingStatusForm: null,
      instruction: null,
      imageUrls: []
    };
  },
  computed: {
    isEditorDisabled() {
      return this.$isCallCenter;
    }
  },
  created() {
    this.setup();
  },
  methods: {
    async setup() {
      await this.setupInstruction();
      this.setupBuildingStatusForm();
      this.setupImageUrls();
    },
    async setupInstruction() {
      const projectId = this.$route.params.id;
      const response =
        await baanRueJaiClient.admin.projects.getInstructionProgress(projectId);

      this.instruction = response.data.result;
    },
    setupBuildingStatusForm() {
      this.buildingStatusForm = this.mapInstructionToForm(this.instruction);
    },
    setupImageUrls() {
      this.imageUrls = this.instruction?.image_urls || [];
    },
    mapInstructionToForm(instruction) {
      if (!instruction) {
        return {
          startAt: null,
          endAt: null,
          updatedAt: null,
          tasks: [],
          status: null,
          overallProgress: null,
          imageFiles: []
        };
      }

      const {
        project_instruction_status: status,
        project_start_at: startAt,
        project_end_at: endAt,
        project_latest_update_at: updatedAt,
        project_overall_progress: overallProgress,
        project_detail_progress: detailProgress
      } = instruction;

      return {
        startAt: this.isoStringToDateTime(startAt),
        endAt: this.isoStringToDateTime(endAt),
        updatedAt: this.isoStringToDateTime(updatedAt),
        tasks: detailProgress.map((p) => ({
          startAt: this.isoStringToDateTime(p.start_at),
          name: p.name,
          progress: p.progress
        })),
        status,
        overallProgress,
        imageFiles: []
      };
    },
    async handleClickSave() {
      if (!this.buildingStatusForm?.status) {
        this.$message.error("กรุณากรอกข้อมูลให้ครบ");

        return;
      }

      try {
        const params = await this.requestParams();
        const projectId = this.$route.params.id;
        const api = this.instruction
          ? baanRueJaiClient.admin.projects.putInstructionProgress
          : baanRueJaiClient.admin.projects.postInstructionProgress;

        await api(projectId, params);

        this.$message.success("บันทึกสำเร็จ");
      } catch (error) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง");
      }
    },
    async requestParams() {
      const {
        status,
        startAt,
        endAt,
        updatedAt,
        overallProgress,
        tasks,
        imageFiles
      } = this.buildingStatusForm;

      const imageFileUrls = await Promise.all(
        imageFiles.map((imageFile) => uploadPublicImage(imageFile))
      );

      return {
        project_id: this.$route.params.id,
        project_instruction_status: status,
        project_start_at: this.dateTimeToIsoString(startAt),
        project_end_at: this.dateTimeToIsoString(endAt),
        project_latest_update_at: this.dateTimeToIsoString(updatedAt),
        project_overall_progress: overallProgress || null,
        project_detail_progress: tasks.map((t) => ({
          name: t.name,
          progress: t.progress || "0",
          start_at: this.dateTimeToIsoString(t.startAt)
        })),
        image_urls: [...this.imageUrls, ...imageFileUrls]
      };
    },
    dateTimeToIsoString(dateTime) {
      if (!dateTime) {
        return null;
      }

      return dateTime.toISO();
    },
    isoStringToDateTime(text) {
      if (!text) {
        return null;
      }

      return DateTime.fromISO(text);
    },
    async handleClickRemove() {
      try {
        await MessageBox.confirm("ยืนยันการลบ", {
          confirmButtonText: "ตกลง",
          cancelButtonText: "ยกเลิก"
        });
      } catch (e) {
        return;
      }

      const projectId = this.$route.params.id;

      try {
        await baanRueJaiClient.admin.projectInstructionProgress.delete(projectId);

        this.instruction = null;
        this.setupBuildingStatusForm();
        this.setupImageUrls();
        this.$message.success("สำเร็จ");
      } catch (error) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง");
      }
    },
    handleRemoveImage(index) {
      this.imageUrls = this.imageUrls.filter(
        (imageUrl, imageUrlIndex) => index !== imageUrlIndex
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.container {
  padding-top: $space;
  padding-bottom: $space;
}

.detail {
  border-right: $border;
}

.label {
  font-size: 14px;
  color: #666;
}

.text {
  font-size: 16px;
  color: black;
}

.project-group {
  display: grid;
  grid-template-columns: 40px 1fr;
  grid-gap: 15px;
  align-items: center;

  > .logo {
    border: $border;
    border-radius: 6px;
  }
}

.cover {
  border: $border;
  border-radius: 6px;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
}

.actions {
  text-align: right;
}

.btn-remove {
  float: left;
}
</style>
